import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useParams } from "react-router-dom";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data?.graph ?? []}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          width={76}
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
          allowDataOverflow
        />
        <Tooltip content={<CustomGraphTooltip yName="B.P" />} />
        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Bar
          dataKey="yValue"
          unit={data?.range?.unit}
          fill="#5FB0B7"
          barSize={20}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const VITALS_CHECKED_1 = "systolicBloodPressure";
const VITALS_CHECKED_2 = "diastolicBloodPressure";

function BloodPressure() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph1,
    isLoading: isLoading1,
    isError: isError1,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED_1,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED_1}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  //get vital signs
  const {
    data: vitalsGraph2,
    isLoading: isLoading2,
    isError: isError2,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED_2,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED_2}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="24"
            height="29"
            viewBox="0 0 24 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.1673 20.3335C12.9753 20.3336 12.7862 20.2862 12.6169 20.1956C12.4476 20.1051 12.3032 19.9741 12.1967 19.8144L10.2098 16.8335H8.50065C8.19123 16.8335 7.89449 16.7106 7.67569 16.4918C7.4569 16.273 7.33398 15.9763 7.33398 15.6669C7.33398 15.3575 7.4569 15.0607 7.67569 14.8419C7.89449 14.6231 8.19123 14.5002 8.50065 14.5002H10.834C11.2237 14.5002 11.5877 14.695 11.8047 15.0194L13.0227 16.8464L15.6232 11.6442C15.7168 11.4575 15.8589 11.2993 16.0345 11.1861C16.2102 11.0729 16.4129 11.009 16.6217 11.0009C16.8305 10.9928 17.0376 11.0408 17.2215 11.14C17.4054 11.2392 17.5593 11.3859 17.6672 11.5649L20.8277 16.8335H22.5007C22.8101 16.8335 23.1068 16.9565 23.3256 17.1752C23.5444 17.394 23.6673 17.6908 23.6673 18.0002C23.6673 18.3096 23.5444 18.6064 23.3256 18.8252C23.1068 19.044 22.8101 19.1669 22.5007 19.1669H20.1673C19.966 19.167 19.7681 19.1149 19.5928 19.0157C19.4176 18.9165 19.2711 18.7737 19.1675 18.601L16.7607 14.59L14.2115 19.6895C14.0248 20.0617 13.655 20.3067 13.2397 20.3324L13.1673 20.3335Z"
              fill="#5FB0B7"
            />
            <path
              d="M20.1673 21.5C19.5632 21.4979 18.9699 21.3401 18.4447 21.0417C17.9194 20.7433 17.4801 20.3145 17.169 19.7966L16.9473 19.435L16.294 20.73C15.7457 21.85 14.6257 22.585 13.389 22.655L13.1673 22.6666C11.989 22.6666 10.904 22.0833 10.2507 21.1033L8.95565 19.1666H8.50065C6.57565 19.1666 5.00065 17.5916 5.00065 15.6666C5.00065 13.7416 6.57565 12.1666 8.50065 12.1666H10.834C11.4407 12.1666 12.024 12.3183 12.5373 12.61L13.5407 10.6033C14.1123 9.45997 15.2557 8.7133 16.539 8.66664C17.0407 8.65497 17.5307 8.73664 17.974 8.9233C15.209 4.57164 11.6973 1.00164 11.4523 0.756636C11.2849 0.599304 11.0637 0.511719 10.834 0.511719C10.6042 0.511719 10.3831 0.599304 10.2157 0.756636C9.80732 1.16497 0.333984 10.8016 0.333984 17.9183C0.333984 23.7516 5.04732 28.5 10.834 28.5C15.3723 28.5 19.2573 25.5716 20.7156 21.5H20.1673Z"
              fill="#5FB0B7"
            />
          </svg>
        }
        title={"Blood Pressure"}
        subTitle={`Systolic and diastolic pressure measured in ${
          !isLoading1 && !isError1 && vitalsGraph1?.data
            ? vitalsGraph1?.data?.range?.unit
            : "mmHg"
        } `}
      />
      {isLoading1 ? (
        <CustomLoader />
      ) : !isError1 ? (
        <Stack
          sx={{
            width: {
              xs: "70vw",
              sm: "50vw",
            },
            height: {
              xs: "35vh",
              sm: "40vh",
              md: "70vh",
            },
          }}
          spacing={2}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
            }}
          >
            *Systolic blood pressure
          </Typography>
          <Graph data={vitalsGraph1.data} />
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.value}
          </Typography>
        </Stack>
      ) : (
        "No data to display"
      )}

      {isLoading2 ? (
        <CustomLoader />
      ) : !isError2 ? (
        <Stack
          mt={4}
          sx={{
            width: {
              xs: "70vw",
              sm: "50vw",
            },
            height: {
              xs: "35vh",
              sm: "40vh",
              md: "70vh",
            },
          }}
          spacing={2}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
            }}
          >
            *Diastolic blood pressure
          </Typography>
          <Graph data={vitalsGraph2.data} />
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        </Stack>
      ) : (
        "No data to display"
      )}
    </Stack>
  );
}

export default BloodPressure;
